import React, { useCallback, useEffect, useState } from 'react';
import { actions, inputs, inputsV2, feedback } from '@ecosystem/newron-design-system';
import { ContextSettingSelectorProps, useEditContextSettings } from '@ecosystem/newron-core';
import { Entity } from '@ecosystem/newron-core/types';
import {
  AddCompetitorLabel,
  BrandCompetitorOptions,
  BrandCompetitorsType,
  BrandCompetitorTypeOptionMap,
  BrandsContextKeys,
  BrandsContextKeyValues,
  BrandTypeMap,
  CompetitorCheckBoxOptions,
  CompetitorTypeOption,
} from './constant';
import {
  findSharedBrandsFromEntity,
  getCompetitorBrandsOptionsBasedOnType,
  getFlatOptionsWithOwned,
  getFormattedBrandCompetitorCategory,
  getFormattedCompetitorBrandOptions,
  getFormattedExcludedBrandOwners,
  getFormattedSharedBrands,
  getFormattedTopNSelection,
  getUpdatedCompetitorBrandOptions,
} from './brandsUtil';
import { InitialSelectedListItem, ListItem } from '@ecosystem/newron-design-system/build/components/listBox/types';
import { TopBrandsRadioSelector } from './TopBrandsRadioSelector';
const { Select } = inputsV2;
const { Button, IconButton, DragAndDrop } = actions;
const { DotsLoader } = feedback;
const { Checkbox, CheckboxState, ListBox } = inputs;

function BrandOptionsSelector({ onClose, onChange }: ContextSettingSelectorProps) {
  /** Existing states retained for NEWE-56083 */
  const [competitorBrands, setCompetitorBrands] = useState<Entity[]>([]);
  const [brandOptions, setBrandOptions] = useState<Entity[]>();
  const [sharedBrands, setSharedBrands] = useState<Entity[]>([]);
  const [highlightedValue, setHighLightedValue] = useState<Entity[]>();

  /** New states created for highlighted modal UI update */
  const [isCompetitorTypeOptionOpen, setIsCompetitorTypeOptionOpen] = useState(false);
  const [competitorTypeOption, setCompetitorTypeOption] = useState<ListItem>(CompetitorTypeOption.Brands);
  const [topValue, setTopValue] = useState<Entity>();
  const [quantityTypeValue, setQuantityTypeValue] = useState<Entity>();
  const [competitorOptionsByCompetitorType, setCompetitorOptionsByCompetitorType] = useState<Entity[]>([]);

  /** New States for the top value options and quantity type options drop down menu */
  const [topValueDropDownOptions, setTopValueDropDownOptions] = useState<Entity[]>([]);
  const [quantityTypeDropDownOptions, setQuantityTypeDropDownOptions] = useState<Entity[]>([]);

  /** New States for the checkboxes */
  const [exactListCheckboxState, setExactListCheckboxState] = useState(CheckboxState.UNCHECKED);
  const [topNCheckboxState, setTopNCheckboxState] = useState(CheckboxState.UNCHECKED);

  const { getContextSettings } = useEditContextSettings();

  const handleCompetitorTypeOptionChange = (option: ListItem) => {
    //null check required for selecting same option again
    if (!option) return;
    setCompetitorTypeOption(option);
    // Reset selected competitors list when competitor type changes
    setCompetitorBrands([]);
  };

  useEffect(() => {
    const fetch = async () => {
      const settings = await getContextSettings(BrandsContextKeyValues);
      const brandOptionsToSet = settings[BrandsContextKeys.BrandLimitingOptionsKey].definition.selection.options.filter(
        v => typeof v !== 'string'
      ) as Entity[];
      // Set brand options for drop down menu
      setBrandOptions(brandOptionsToSet);
      setSharedBrands(findSharedBrandsFromEntity(brandOptionsToSet));
      // Set top value options for drop down menu
      const topValueOptions = settings[BrandsContextKeys.TopValueOptionsKey].definition.selection.options as Entity[];
      setTopValueDropDownOptions(topValueOptions);
      setTopValue(topValueOptions[0]);
      // Set quantity type options for drop down menu
      const quantityTypeOptions = settings[BrandsContextKeys.QuantityTypeOptionsKey].definition.selection
        .options as Entity[];
      setQuantityTypeDropDownOptions(quantityTypeOptions);
      setQuantityTypeValue(quantityTypeOptions[0]);

      //Restore the previous selections within the modal or default to one there isn't any
      const defaultCompetitorType = settings[BrandsContextKeys.BrandCompetitorTypeKey]?.values[0]?.value
        ? BrandCompetitorTypeOptionMap[settings[BrandsContextKeys.BrandCompetitorTypeKey]?.values[0]?.value]
        : CompetitorTypeOption.Brands;
      setHighLightedValue(settings[BrandsContextKeys.HighlightedBrandKey].values);
      setCompetitorTypeOption(defaultCompetitorType);

      const brandCompetitorCategory =
        settings[BrandsContextKeys.BrandCompetitorCategoryKey]?.values.map(v => v.value) ?? [];

      //If there is custom competitor list, set to Exact List, else set to Top N with previous selections
      if (brandCompetitorCategory.includes(BrandCompetitorOptions.ExactList)) {
        // Set previous custom competitor list
        setExactListCheckboxState(CheckboxState.CHECKED);
        setCompetitorBrands(settings[BrandsContextKeys.CompetitorBrandListKey].values);
      }
      if (
        brandCompetitorCategory.includes(BrandCompetitorOptions.TopBrandOwners) ||
        brandCompetitorCategory.includes(BrandCompetitorOptions.TopBrands)
      ) {
        // Parse the previous topNSelection to restore the previous selections
        const topNSelection =
          settings[BrandsContextKeys.TopNSelectionKey]?.values[0]?.value ??
          `${defaultCompetitorType.value}.top5.${quantityTypeOptions[0].value}`;
        const [_, topValue, quantityTypeValue] = topNSelection.split('.');
        setTopNCheckboxState(CheckboxState.CHECKED);
        setTopValue({ label: topValue.replace('top', ''), value: topValue });
        setQuantityTypeValue({ label: quantityTypeValue, value: quantityTypeValue });
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCompetitorOptions = useCallback(
    (brand: Entity) => {
      const flattenedOptions = getFlatOptionsWithOwned(brandOptions);
      const brandWithType: Entity = flattenedOptions.find(opt => opt.value === brand.value);

      setHighLightedValue(prev => {
        const current = prev?.[0];
        if (!current || current.value !== brandWithType.value || current.type !== brandWithType.type) {
          return [{ ...current, type: brandWithType.type }];
        }
        return prev;
      });
    },
    [brandOptions]
  );

  useEffect(() => {
    if (highlightedValue?.length) {
      updateCompetitorOptions(highlightedValue[0]);
    }
  }, [highlightedValue, updateCompetitorOptions]);

  /**
  This useEffect is to update the custom competitor options list based on the selected competitor type (brands or brand owners)
  - If brands is selected, it only displays OWNED and INDEPENDENT types
  - If brand owners is selected, it only displays OWNER type
  - Note that when focus brand is changed, it will also trigger this useEffect to update the competitor options list
   */
  useEffect(() => {
    const updatedCompetitorOptions = getUpdatedCompetitorBrandOptions(brandOptions, competitorBrands, highlightedValue);
    const updatedCompetitorOptionsByCompetitorType = getCompetitorBrandsOptionsBasedOnType(
      competitorTypeOption,
      updatedCompetitorOptions
    );
    setCompetitorOptionsByCompetitorType(updatedCompetitorOptionsByCompetitorType);
  }, [brandOptions, competitorBrands, highlightedValue, competitorTypeOption]);

  const handleHighlightedChange = (value: string) => {
    const competitorTypeMap = {
      [BrandCompetitorsType.OWNER]: CompetitorTypeOption.BrandOwners,
      [BrandCompetitorsType.INDEPENDENT]: CompetitorTypeOption.Brands,
      [BrandCompetitorsType.OWNED]: CompetitorTypeOption.Brands,
    };
    if (typeof value !== 'string') return;
    const flatOptionsWithOwned = getFlatOptionsWithOwned(brandOptions);
    const brand: Entity = flatOptionsWithOwned.find(opt => opt.value === value);
    if (!brand) return;
    setHighLightedValue([brand]);
    const updatedCompetitorTypeOption = competitorTypeMap[brand.type];
    setCompetitorTypeOption(updatedCompetitorTypeOption);
    // Reset selected competitors list when competitor type changes
    setCompetitorBrands([]);
  };

  const handleAddCompetitorBrand = (value: string) => {
    if (typeof value !== 'string') {
      return;
    }
    const flatOptionsWithOwned = getFlatOptionsWithOwned(brandOptions);
    const brand = flatOptionsWithOwned.find(opt => opt.value === value);

    if (brand && !competitorBrands.some(b => b.value === brand.value)) {
      setCompetitorBrands([...competitorBrands, brand]);
    }
  };

  const handleRemoveCompetitorBrand = (value: string) => {
    const updatedSelectedCompetitorBrands = competitorBrands.filter(e => e.value !== value);
    setCompetitorBrands(updatedSelectedCompetitorBrands);
  };

  const clearCompetitorBrands = () => {
    setCompetitorBrands([]);
  };
  const handleApply = () => {
    const trueFlag = [{ value: 'true', label: 'true' }];
    const competitorBrandList: Entity[] = [...competitorBrands];

    const brandCompetitorType = BrandTypeMap[competitorTypeOption.value as string];
    const isCompetitorBrandOwner =
      competitorBrands.some(b => b.type === BrandCompetitorsType.OWNER) ||
      highlightedValue[0].type === BrandCompetitorsType.OWNER;
    const input = {
      highlightedBrand: highlightedValue,
      competitorBrandOption: getFormattedCompetitorBrandOptions(
        { topNCheckboxState, exactListCheckboxState },
        topValue,
        quantityTypeValue
      ),
      competitorBrandList,
      is_brand_owner: highlightedValue[0].type === BrandCompetitorsType.OWNER ? trueFlag : [],
      excluded_brand_owners: getFormattedExcludedBrandOwners(highlightedValue, brandCompetitorType, sharedBrands),
      shared_brands: getFormattedSharedBrands(highlightedValue, brandCompetitorType, sharedBrands),
      focus_brand: highlightedValue,
      brand_competitor_type: [{ value: brandCompetitorType, label: brandCompetitorType }],
      isCompetitorBrandOwner: isCompetitorBrandOwner ? trueFlag : [],
      brandCompetitorCategory: getFormattedBrandCompetitorCategory(
        { topNCheckboxState, exactListCheckboxState },
        competitorTypeOption
      ),
      topNSelection: getFormattedTopNSelection(topNCheckboxState, competitorTypeOption, topValue, quantityTypeValue),
    };
    onChange(input);
    onClose();
  };

  const handleExactListCheckboxChange = (state: typeof CheckboxState[keyof typeof CheckboxState]) => {
    if (state === CheckboxState.UNCHECKED) {
      setCompetitorBrands([]);
    }
    setExactListCheckboxState(state);
  };

  const handleTopNCheckboxChange = (state: typeof CheckboxState[keyof typeof CheckboxState]) => {
    setTopNCheckboxState(state);
  };

  if (!brandOptions) {
    return (
      <div className="h-32">
        <DotsLoader active stretch />
      </div>
    );
  }

  const preventApply = exactListCheckboxState === CheckboxState.CHECKED && !competitorBrands?.length;
  const handleRearrange = event => {
    const newArray: Entity[] = [];
    const newIndexOrder = event.newIndexOrder;
    newIndexOrder.forEach((index: number) => {
      newArray.push(competitorBrands[index]);
    });
    setCompetitorBrands(newArray);
  };
  return (
    <div className="overflow-y-auto m-2.5">
      <div className="py-3 px-4">
        <div className="text-heading text-sm pb-2">
          All the brands / brand owners, except selected here, will be aggregated as "Others".
        </div>
        <div className="font-bold text-heading text-sm pb-2">Focus brand / brand owner</div>
        <div className="w-11/12">
          <Select
            value={highlightedValue?.[0].value}
            options={brandOptions || []}
            onChange={handleHighlightedChange}
            combobox={true}
            hasSectionDivider={true}
            sectionDivider="thin"
            isOptionIndented={true}
          />
        </div>
      </div>
      <div className="py-2 px-4">
        <div className="inline-flex">
          <div className="font-bold text-heading text-sm mt-2.5 ml-2.5">Competitor</div>
          <ListBox
            testId="competitor-type-listbox"
            list={[CompetitorTypeOption.Brands, CompetitorTypeOption.BrandOwners]}
            isOpen={isCompetitorTypeOptionOpen}
            setIsOpen={setIsCompetitorTypeOptionOpen}
            sectionDivider="thin"
            width={300}
            onChange={handleCompetitorTypeOptionChange}
            value={competitorTypeOption as InitialSelectedListItem}
          >
            <Button onClick={() => setIsCompetitorTypeOptionOpen(!isCompetitorTypeOptionOpen)} mode="simple">
              {competitorTypeOption.label}
              <Button.Icon>
                <Button.Icon.icons.ChevronDown />
              </Button.Icon>
            </Button>
          </ListBox>
        </div>
        <div
          className="pl-2 py-2 grid"
          data-test-id="competitor-selection-section"
          data-testid="competitor-selection-section"
        >
          <div className="inline-flex">
            <div>
              <Checkbox
                label=""
                onChange={handleExactListCheckboxChange}
                state={exactListCheckboxState}
                value={CompetitorCheckBoxOptions.ExactList}
                testId="testid-checkbox-exact-list-option"
                id="checkbox-exact-list-option"
              />
            </div>
            <div className="ml-4 w-10/12">
              <div className="inline-flex">
                <div>Select competitors</div>
              </div>
              {exactListCheckboxState === CheckboxState.CHECKED && (
                <div>
                  <div className="flex mb-2.5">
                    <div className="mt-2.5 mr-2.5 w-10/12">
                      <Select
                        options={competitorOptionsByCompetitorType}
                        onChange={handleAddCompetitorBrand}
                        placeholder={AddCompetitorLabel[competitorTypeOption.value as string]}
                        combobox={true}
                        hasSectionDivider={true}
                        sectionDivider="thin"
                        isOptionIndented={competitorTypeOption.value === CompetitorTypeOption.Brands.value}
                      />
                    </div>
                    <div className="mt-3.5">
                      <Button disabled={!competitorBrands?.length} onClick={clearCompetitorBrands} mode="secondary">
                        Clear all
                      </Button>
                    </div>
                  </div>
                  <div className="mb-2 overflow-y-auto overscroll-contain" style={{ maxHeight: '152px' }}>
                    <DragAndDrop onChange={handleRearrange} onKeyDown={() => {}}>
                      {competitorBrands?.map(b => (
                        <div key={b.value} className={'text-sm flex items-center pr-2'}>
                          <span className="flex-grow ml-8 mt-1">{b.label}</span>
                          <div data-testid={`remove-competitor-${b.value}`}>
                            <IconButton onClick={() => handleRemoveCompetitorBrand(b.value)}>
                              <IconButton.icons.RemoveOutline />
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </DragAndDrop>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="inline-flex">
            <div className="mt-2">
              <Checkbox
                label=""
                onChange={handleTopNCheckboxChange}
                state={topNCheckboxState}
                value={CompetitorCheckBoxOptions.TopN}
                testId="testid-checkbox-top-n-option"
                id="checkbox-top-n-option"
              />
            </div>

            <div className="ml-4">
              <TopBrandsRadioSelector
                selectedTopValue={topValue}
                selectedQuantityType={quantityTypeValue}
                topValueOptions={topValueDropDownOptions}
                quantityTypeOptions={quantityTypeDropDownOptions}
                setTopValue={setTopValue}
                setQuantityType={setQuantityTypeValue}
                competitorTypeOption={competitorTypeOption.label}
                disabled={topNCheckboxState === CheckboxState.UNCHECKED}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between px-4 py-3 border-t border-default">
        <Button mode="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={preventApply} onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  );
}

export { BrandOptionsSelector };
